<template>
  <div class="analyze-cpw">
    <div class="container">

      <!-- Hero: 平均 CPW -->
      <section class="hero-section">
        <div class="hero-num">${{ avgCpw }}</div>
        <div class="hero-label fs-14px">平均每次穿搭成本</div>
        <p class="hero-msg fs-14px">{{ heroMsg }}</p>
      </section>

      <!-- 三個指標 -->
      <div class="stat-grid">
        <section class="stat-card">
          <div class="stat-title fs-14px">總投資</div>
          <div class="stat-value">
            <span class="unit">$</span><span class="num">{{ totalInvestmentDisplay }}</span>
          </div>
          <div class="stat-sub fs-12px">{{ pricedCloths.length }} 件已知價格</div>
        </section>
        <section class="stat-card">
          <div class="stat-title fs-14px">已穿過</div>
          <div class="stat-value">
            <span class="num">{{ wornPricedCount }}</span><span class="unit">件</span>
          </div>
          <div class="stat-sub fs-12px">已實現價值</div>
        </section>
        <section class="stat-card">
          <div class="stat-title fs-14px">尚未穿過</div>
          <div class="stat-value">
            <span class="num">{{ unwornCloths.length }}</span><span class="unit">件</span>
          </div>
          <div class="stat-sub fs-12px">未實現的投資</div>
        </section>
      </div>

      <!-- 最划算 top 5 -->
      <section class="review-card" v-if="topMostWorth.length > 0">
        <h3 class="section-title fs-16px fw-bold">最划算的單品</h3>
        <p class="card-desc fs-12px">穿得多、CP 值最高</p>
        <div class="cloth-list">
          <div v-for="(c, i) in topMostWorth" :key="c.serial" class="cloth-row">
            <div class="rank fw-bold">{{ i + 1 }}</div>
            <div class="cloth-thumb" :style="thumbStyle(c)"></div>
            <div class="cloth-info">
              <div class="cloth-name fs-14px fw-bold">{{ c.name || '未命名單品' }}</div>
              <div class="cloth-meta fs-12px">穿了 {{ c.wearCount }} 次 · 原價 ${{ c.price }}</div>
            </div>
            <div class="cpw-tag good fs-12px fw-bold">${{ c.cpw.toFixed(0) }}/次</div>
          </div>
        </div>
      </section>

      <!-- CP 值最低 top 5 -->
      <section class="review-card" v-if="topLeastWorth.length > 0">
        <h3 class="section-title fs-16px fw-bold">CP 值最低的單品</h3>
        <p class="card-desc fs-12px">花了不少但穿得少 — 多穿幾次或考慮轉賣</p>
        <div class="cloth-list">
          <div v-for="(c, i) in topLeastWorth" :key="c.serial" class="cloth-row">
            <div class="rank fw-bold">{{ i + 1 }}</div>
            <div class="cloth-thumb" :style="thumbStyle(c)"></div>
            <div class="cloth-info">
              <div class="cloth-name fs-14px fw-bold">{{ c.name || '未命名單品' }}</div>
              <div class="cloth-meta fs-12px">穿了 {{ c.wearCount }} 次 · 原價 ${{ c.price }}</div>
            </div>
            <div class="cpw-tag bad fs-12px fw-bold">${{ c.cpw.toFixed(0) }}/次</div>
          </div>
        </div>
      </section>

      <!-- 尚未穿過 list -->
      <section class="review-card" v-if="unwornCloths.length > 0">
        <h3 class="section-title fs-16px fw-bold">尚未穿過的單品</h3>
        <p class="card-desc fs-12px">總共 ${{ unwornInvestment }} 還沒被使用過 — 找個機會穿出去吧！</p>
        <div class="cloth-list">
          <div v-for="c in unwornDisplay" :key="c.serial" class="cloth-row">
            <div class="cloth-thumb small" :style="thumbStyle(c)"></div>
            <div class="cloth-info">
              <div class="cloth-name fs-14px">{{ c.name || '未命名單品' }}</div>
              <div class="cloth-meta fs-12px">${{ c.price }}</div>
            </div>
          </div>
          <button
            v-if="hasMoreUnworn"
            class="load-more-btn fs-14px"
            @click="loadMoreUnworn"
          >
            再顯示 {{ nextLoadCount }} 件（剩 {{ unwornCloths.length - unwornDisplay.length }} 件）
          </button>
        </div>
      </section>

      <!-- Empty -->
      <section class="empty-state" v-if="pricedCloths.length === 0">
        <p class="fs-14px">還沒有設定價格的單品。在新增／編輯單品時填上價格，就能看到 CP 值分析。</p>
      </section>

    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'AnalyzeCpw',
  data() {
    return {
      unwornDisplayCount: 20,
    };
  },
  computed: {
    ...mapGetters(['clothWithWearCount']),

    cloths() {
      return this.clothWithWearCount || [];
    },
    pricedCloths() {
      return this.cloths.filter(c =>
        !c.isDeleted &&
        c.recycleType === -1 &&
        typeof c.price === 'number' &&
        c.price > 0
      );
    },
    wornPricedCloths() {
      return this.pricedCloths
        .filter(c => c.wearCount > 0)
        .map(c => ({ ...c, cpw: c.price / c.wearCount }));
    },
    wornPricedCount() {
      return this.wornPricedCloths.length;
    },
    unwornCloths() {
      return this.pricedCloths.filter(c => c.wearCount === 0 || !c.wearCount);
    },
    unwornSorted() {
      // 按原價由高到低（最該穿的優先）
      return [...this.unwornCloths].sort((a, b) => b.price - a.price);
    },
    unwornDisplay() {
      return this.unwornSorted.slice(0, this.unwornDisplayCount);
    },
    hasMoreUnworn() {
      return this.unwornCloths.length > this.unwornDisplay.length;
    },
    nextLoadCount() {
      // 還剩多少未顯示，最多顯示「再顯示 20 件」
      const remaining = this.unwornCloths.length - this.unwornDisplay.length;
      return Math.min(20, remaining);
    },
    unwornInvestment() {
      return this.unwornCloths.reduce((s, c) => s + (c.price || 0), 0).toLocaleString();
    },
    totalInvestment() {
      return this.pricedCloths.reduce((s, c) => s + (c.price || 0), 0);
    },
    totalInvestmentDisplay() {
      return this.totalInvestment.toLocaleString();
    },
    avgCpw() {
      if (this.wornPricedCloths.length === 0) return 0;
      const sum = this.wornPricedCloths.reduce((s, c) => s + c.cpw, 0);
      return Math.round(sum / this.wornPricedCloths.length);
    },
    topMostWorth() {
      return [...this.wornPricedCloths]
        .sort((a, b) => a.cpw - b.cpw)
        .slice(0, 5);
    },
    topLeastWorth() {
      return [...this.wornPricedCloths]
        .sort((a, b) => b.cpw - a.cpw)
        .slice(0, 5);
    },
    heroMsg() {
      const c = this.avgCpw;
      if (this.wornPricedCloths.length === 0) return '還沒有可計算的單品 — 開始紀錄穿搭就會有數字了';
      if (c < 50) return '太厲害了！你把每件衣服都穿到值回票價';
      if (c < 200) return '挺不錯的，整體 CP 值健康';
      if (c < 500) return '還可以更好 — 多穿幾次冷門單品吧';
      return '建議檢視看看哪些單品其實不太適合你';
    },
  },
  methods: {
    thumbStyle(cloth) {
      const url = (cloth.imgs && cloth.imgs[0]) || '';
      return url ? { backgroundImage: `url(${url})` } : { backgroundColor: '#F4F4F4' };
    },
    loadMoreUnworn() {
      this.unwornDisplayCount += 20;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/basic";

.analyze-cpw {
  padding: .5rem 0 2rem;
}

.hero-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .25rem;
  padding: 1.5rem 0 2rem;
  text-align: center;
}
.hero-num {
  font-size: 3rem;
  font-weight: bold;
  color: #2A7A6B;
  line-height: 1;
}
.hero-label {
  color: $typography-primary-default;
  margin-top: .5rem;
}
.hero-msg {
  color: $typography-secondary-default;
  margin: .5rem 0 0;
}

.stat-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: .75rem;
  margin-bottom: 1.5rem;
  @media (min-width: 576px) {
    grid-template-columns: repeat(3, 1fr);
  }
}
.stat-card {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 1rem 1.25rem;

  .stat-title {
    color: $typography-secondary-default;
    margin-bottom: .25rem;
  }
  .stat-value {
    color: $typography-primary-default;
    margin-bottom: .25rem;
    .num {
      font-size: 1.75rem;
      font-weight: bold;
    }
    .unit {
      font-size: 1rem;
      color: $typography-secondary-default;
      margin: 0 2px;
    }
  }
  .stat-sub {
    color: $typography-secondary-default;
  }
}

.review-card {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 1.25rem;
  margin-bottom: 1rem;

  .section-title {
    margin: 0 0 .25rem;
    color: $typography-primary-default;
  }
  .card-desc {
    color: $typography-secondary-default;
    margin: 0 0 1rem;
  }
}

.cloth-list {
  display: flex;
  flex-direction: column;
  gap: .75rem;
}
.cloth-row {
  display: flex;
  align-items: center;
  gap: .75rem;

  .rank {
    flex: 0 0 1.25rem;
    font-size: 1rem;
    color: $color-primary;
    text-align: center;
  }
  .cloth-thumb {
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    background-color: #F4F4F4;
    border-radius: .5rem;
    border: 1px solid $color-border;
    &.small {
      flex: 0 0 44px;
      width: 44px;
      height: 44px;
    }
  }
  .cloth-info {
    flex: 1 1 auto;
    min-width: 0;
    .cloth-name {
      color: $typography-primary-default;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .cloth-meta {
      color: $typography-secondary-default;
      margin-top: 2px;
    }
  }
  .cpw-tag {
    flex: 0 0 auto;
    padding: 4px 10px;
    border-radius: 999px;
    &.good {
      background-color: rgba(125, 196, 183, .15);
      color: #2A7A6B;
    }
    &.bad {
      background-color: rgba(222, 97, 103, .15);
      color: #B73B41;
    }
  }
}
.load-more-btn {
  display: block;
  width: 100%;
  margin-top: .75rem;
  padding: .625rem 1rem;
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .5rem;
  color: $typography-primary-default;
  cursor: pointer;
  transition: background-color .15s ease;
  &:hover {
    background-color: rgba(0, 0, 0, .03);
  }
}

.empty-state {
  background-color: #fff;
  border: 1px solid $color-border;
  border-radius: .75rem;
  padding: 3rem 1.25rem;
  text-align: center;
  color: $typography-secondary-default;
  p { margin: 0; }
}
</style>
